"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { env } from "next-runtime-env";
import { type ReactNode, useEffect, useRef } from "react";
import { AuthLoadingScreen } from "@/components/AuthLoadingScreen/AuthLoadingScreen";
import { AuthNotConfiguredState } from "@/components/AuthNotConfiguredState/AuthNotConfiguredState";
import { useSilentTidFrameCheck } from "@/components/Trimble/hooks/useSilentTidFrameCheck";
import { localeFromPath, stripLocalePrefix } from "@/lib/auth/stripLocalePrefix";
import { clearAuthBackendCookie, getAuthBackendCookie } from "@/lib/authBackend/cookie";
import { setResolvedBackend } from "@/lib/authBackend/resolve";
import { tidClient } from "@/lib/authBackend/trimble/client";
import { reportAuthFailure } from "@/lib/authBackend/trimble/telemetry";
import { POST_LOGOUT_SESSION_KEY } from "@/lib/authBackend/trimble/tid/client";
import { TIDProvider, useAuth } from "@/lib/authBackend/trimble/tid/context";
import { buildProcoreTidLoginUrl, shouldUseProcoreTidPopup } from "@/lib/procore/tidPopup";
import { AuthBackendContext, AuthBackendReadyContext, useAuthBackendId } from "./AuthBackendContext";

// Nothing reads dc.auth-backend any more; clear the 90-day leftover from browsers that set it.
function clearStaleAuthBackendCookie(): void {
  if (typeof window === "undefined") return;
  if (getAuthBackendCookie()) clearAuthBackendCookie();
}

// usePathname() is locale-prefixed; keep that prefix so /login renders in the user's language
// instead of falling back to the proxy's cookie-or-default guess.
function loginPathFor(pathname: string): string {
  const locale = localeFromPath(pathname);
  return locale ? `/${locale}/login` : "/login";
}

// Blocks rendering on protected routes until Trimble auth state is known.
// On refresh, tokens are in-memory only; loginWithRedirect triggers silent
// SSO via Trimble's session cookie — typically resolves within ~500ms.
export function AuthGate(t0) {
  const $ = _c(23);
  const {
    children
  } = t0;
  const backend = useAuthBackendId();
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    client
  } = useAuth();
  const pathname = usePathname();
  const {
    startSilentCheck
  } = useSilentTidFrameCheck();
  const wasAuthenticated = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /^(\/[a-z]{2})?\/(login|callback|logout-callback|resolve|register-public)(\/|$)/;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pathname) {
    t2 = t1.test(pathname);
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isUngatedRoute = t2;
  let t3;
  let t4;
  if ($[3] !== backend || $[4] !== client || $[5] !== isAuthenticated || $[6] !== isLoading || $[7] !== isUngatedRoute || $[8] !== loginWithRedirect || $[9] !== pathname || $[10] !== startSilentCheck) {
    t3 = () => {
      if (backend !== "trimble") {
        return;
      }
      if (isAuthenticated) {
        wasAuthenticated.current = true;
        return;
      }
      if (wasAuthenticated.current || isUngatedRoute || isLoading) {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }
      if (window.sessionStorage.getItem(POST_LOGOUT_SESSION_KEY)) {
        window.location.replace(loginPathFor(pathname));
        return;
      }
      if (shouldUseProcoreTidPopup(pathname)) {
        startSilentCheck(pathname, window.location.search, {
          onComplete: session => {
            ;
            try {
              client.adoptSession(session);
            } catch (t5) {
              const err = t5;
              reportAuthFailure("session_adopt", err, {
                pathname
              });
              window.location.replace(buildProcoreTidLoginUrl(pathname, window.location.search));
            }
          },
          onFail: () => {
            window.location.replace(buildProcoreTidLoginUrl(pathname, window.location.search));
          }
        });
        return;
      }
      const featureBranchName = env("NEXT_PUBLIC_FEATURE_BRANCH_NAME");
      const locale = localeFromPath(pathname);
      loginWithRedirect({
        redirectTo: `${stripLocalePrefix(pathname)}${window.location.search}`,
        silent: true,
        ...(locale ? {
          locale
        } : {}),
        ...(featureBranchName ? {
          featureBranch: featureBranchName
        } : {})
      });
    };
    t4 = [backend, isLoading, isAuthenticated, isUngatedRoute, loginWithRedirect, pathname, client, startSilentCheck];
    $[3] = backend;
    $[4] = client;
    $[5] = isAuthenticated;
    $[6] = isLoading;
    $[7] = isUngatedRoute;
    $[8] = loginWithRedirect;
    $[9] = pathname;
    $[10] = startSilentCheck;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[13] !== backend || $[14] !== client || $[15] !== pathname) {
    t5 = () => {
      if (backend !== "trimble") {
        return;
      }
      return client.subscribeToRemoteLogout(() => {
        window.location.replace(shouldUseProcoreTidPopup(pathname) ? buildProcoreTidLoginUrl(pathname, window.location.search) : loginPathFor(pathname));
      });
    };
    t6 = [backend, client, pathname];
    $[13] = backend;
    $[14] = client;
    $[15] = pathname;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  if (backend !== "trimble") {
    let t7;
    if ($[18] !== children) {
      t7 = <>{children}</>;
      $[18] = children;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  if (!isUngatedRoute && (isLoading || !isAuthenticated)) {
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AuthLoadingScreen />;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  let t7;
  if ($[21] !== children) {
    t7 = <>{children}</>;
    $[21] = children;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
export function AuthBackendModeProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEffect(_temp2, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(_temp3, t3);
  if (!tidClient) {
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AuthNotConfiguredState />;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  let t4;
  if ($[4] !== children) {
    t4 = <TIDProvider client={tidClient}><AuthBackendContext.Provider value="trimble"><AuthBackendReadyContext.Provider value={true}><AuthGate>{children}</AuthGate></AuthBackendReadyContext.Provider></AuthBackendContext.Provider></TIDProvider>;
    $[4] = children;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function _temp3() {
  if (tidClient) {
    return;
  }
  reportAuthFailure("not_configured", new Error("tidClient is null at the auth provider"));
}
function _temp2() {
  setResolvedBackend("trimble");
}
function _temp() {
  clearStaleAuthBackendCookie();
}